import { reloadAsync } from "expo-updates";

import { clearAllWebSessionRouteParams } from "@/crossPlatform/expo-router";
import useHelpCenter from "@/hooks/useHelpCenter";
import usePushNotifications from "@/hooks/usePushNotifications";
import useSendbirdSDK from "@/hooks/useSendbirdSDK";
import { MmkvStorage } from "@/hooks/useUnencryptedStorage";
import useUserIdentitySync from "@/hooks/useUserIdentitySync";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useResetApiStateMutation } from "@/store/api";
import { useResetDispatch } from "@/store/reducers/reset";
import { isWeb } from "@/utils/platform.util";

interface UseClearCache {
  clearCache: () => Promise<void>;
}

const useClearCache = (): UseClearCache => {
  const { reset: resetAnalytics } = useAnalytics();
  const { resetAllReduxSlices } = useResetDispatch();
  const [resetApiState] = useResetApiStateMutation();
  const sendbirdSDK = useSendbirdSDK();
  const { pushToken } = usePushNotifications();
  const { logoutHelpCenter } = useHelpCenter();
  const { resetUserIdentity } = useUserIdentitySync();
  const { captureException, captureMessage } = useAppMonitor();

  const clearCache = async (): Promise<void> => {
    try {
      await logoutHelpCenter().catch((err) => {
        captureException(err);
        captureMessage("Failure logging out of help center during cache clear");
      });

      await sendbirdSDK
        .disconnectSendbird({
          pushToken,
          shouldUnregisterPushToken: false,
        })
        .catch((err) => {
          captureException(err);
          captureMessage("Failure disconnecting Sendbird during cache clear");
        });

      resetAllReduxSlices();

      await resetApiState().catch((err) => {
        captureException(err);
        captureMessage("Failure resetting API state during cache clear");
      });

      resetAnalytics();
      resetUserIdentity();

      try {
        MmkvStorage.clearAll();
      } catch (err) {
        captureException(err);
        captureMessage("Failure clearing MMKV storage during cache clear");
      }

      if (isWeb()) {
        clearAllWebSessionRouteParams();
        globalThis.location.reload();
      } else {
        await reloadAsync();
      }
    } catch (caughtErr) {
      captureException(caughtErr);
      captureMessage("Failure during cache clear");
      throw caughtErr;
    }
  };

  return {
    clearCache,
  };
};

export default useClearCache;
